import React, { useEffect, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Spinner } from 'react-bootstrap'
import { Button, Card, CardBody, CardHeader, Col, Form, FormGroup, FormText, Input, Row } from 'reactstrap'
import SetTitle from '../../utils/set_title'

import { api } from '../../utils/index'
import { ToastContainer } from 'react-toastify'
import { notify } from '../../utils/notify'
import FileInput from '../../components/molecules/FileInput'
import { useFileUpload } from '../../hooks'
import CharCheckModal from '../../components/molecules/CharCheckModal'
import GPTEstimateModal from '../../components/molecules/GPTEstimateModal'
import Header from '../../components/Headers/Header'
import { DEFAULT_QUESTION_PROMPT } from './defaultPrompt'
import TagGeneratedQuestionsModal from './TagGeneratedQuestionsModal'
import LLMResumeFlowModal from './LLMResumeFlowModal'
import { getLLMVisibilityClientName } from './clienteDisplay'
import ClienteField from './ClienteField'
import LocalizationFields from './LocalizationFields'
import auth from '../../auth/service'
import './LLMVisibilityWorkflow.css'

const LLMVisibilityForm = () => {
  SetTitle('Report LLM Visibility')
  const isWebranking = auth.userGroup === 'Webranking'
  const { id } = useParams()
  const [name, setName] = useState('')
  const [brand, setBrand] = useState('')
  const [clienti, setClienti] = useState([])
  const [selectedClientId, setSelectedClientId] = useState('')
  const [country, setCountry] = useState('IT')
  const [language, setLanguage] = useState('it')
  const [file, setFile] = useState(null)
  const [useGpt, setUseGpt] = useState(false)
  const [useGemini, setUseGemini] = useState(false)
  const [usePerplexity, setUsePerplexity] = useState(false)
  const [useDataforseo, setUseDataforseo] = useState(false)
  const [customPrompt, setCustomPrompt] = useState(DEFAULT_QUESTION_PROMPT)
  const [reportStatus, setReportStatus] = useState('')
  const [taggingToolReports, setTaggingToolReports] = useState([])
  const [isTaggingModalOpen, setIsTaggingModalOpen] = useState(false)
  const [isSubmittingTagging, setIsSubmittingTagging] = useState(false)
  const [taggingMode, setTaggingMode] = useState('CLONE')
  const [selectedTaggingReportId, setSelectedTaggingReportId] = useState('')
  const [connectedTaggingId, setConnectedTaggingId] = useState('')
  const [availableActions, setAvailableActions] = useState([])
  const [isResumeFlowModalOpen, setIsResumeFlowModalOpen] = useState(false)
  const [resumeFlowStatus, setResumeFlowStatus] = useState(null)
  const [isLoadingResumeFlowStatus, setIsLoadingResumeFlowStatus] = useState(false)
  const [resumingFlowNodeKey, setResumingFlowNodeKey] = useState('')
  const [taggingOptions, setTaggingOptions] = useState({
    forceRegenerateQuestions: false,
    forceNewClone: false,
    forceRetag: false,
  })

  const { upload, loading } = useFileUpload()

  const pendingActionRef = useRef({ action: 'create', questionsOnly: false, flowNode: null })

  const [showCharCheck, setShowCharCheck] = useState(false)
  const [errCharList, setErrCharList] = useState([])
  const [acceptedCharCheck, setAcceptedCharCheck] = useState(false)
  const [showGptCheck, setShowGptCheck] = useState(false)
  const [gptEstimateParams, setGptEstimateParams] = useState({
    n_keywords: -1,
    total_tokens: -1,
    estimate: -1,
    setup_estimate: -1,
    first_run_estimate: -1,
    phase_estimates: {},
  })
  const [acceptedGptCheck, setAcceptedGptCheck] = useState(false)
  const estimateSubmitInFlightRef = useRef(false)

  const history = useHistory()

  const validateForm = (questionsOnly = false) => {
    if (!name || (!file && !id)) {
      notify("Inserisci il nome dell'analisi e carica un file.", 'error')
      return false
    }
    if (isWebranking && !selectedClientId) {
      notify('Seleziona un cliente.', 'error')
      return false
    }
    if (!country || !language) {
      notify('Seleziona country e lingua.', 'error')
      return false
    }
    if (!questionsOnly && !useGpt && !useGemini && !usePerplexity) {
      notify('Seleziona almeno un modello LLM (GPT, Gemini o Perplexity).', 'error')
      return false
    }
    if (customPrompt && !customPrompt.includes('{keyword}')) {
      notify("Il prompt personalizzato deve contenere il segnaposto {keyword}.", 'error')
      return false
    }
    return true
  }

  const buildFormData = (questionsOnly = false, gptEstimateAccepted = false) => {
    const formData = new FormData()
    formData.append('name', name)
    formData.append('brand', brand)
    if (selectedClientId) {
      formData.append('cliente', selectedClientId)
    }
    formData.append('country', country)
    formData.append('language', language)
    if (file) {
      formData.append('file', file)
    }
    formData.append('gpt', useGpt)
    formData.append('gemini', useGemini)
    formData.append('perplexity', usePerplexity)
    formData.append('use_dataforseo', useDataforseo)
    formData.append('custom_prompt', customPrompt)
    if (questionsOnly) {
      formData.append('questions_only', true)
    } else {
      formData.append('gpt_estimate_accepted', gptEstimateAccepted)
    }
    return formData
  }

  const buildResumeFlowFormData = (llm, node, gptEstimateAccepted = false) => {
    const formData = buildFormData(false, gptEstimateAccepted)
    formData.append('llm', llm)
    formData.append('node', node)
    return formData
  }

  const handleActionError = (err, questionsOnly = false) => {
    if (err.response?.data?.message?.includes('ERROR Encoding keywords') && !id) {
      setErrCharList(err.response.data?.errors)
      setShowCharCheck(true)
      return
    }

    if (!questionsOnly && err.response?.data?.message?.includes('GPT Estimate')) {
      setGptEstimateParams(err.response.data?.estimates)
      setShowGptCheck(true)
      return
    }

    notify(err.response?.data?.error || 'Richiesta fallita.', 'warning')
    setAcceptedCharCheck(false)
    setAcceptedGptCheck(false)
  }

  const handleCreate = async (event, questionsOnly = false, gptEstimateAccepted = false) => {
    if (event) event.preventDefault()

    if (!validateForm(questionsOnly)) {
      return
    }

    if (!questionsOnly && !gptEstimateAccepted) {
      pendingActionRef.current = { action: 'create', questionsOnly: false, flowNode: null }
    }

    try {
      await upload('post', 'llm-visibility/', buildFormData(questionsOnly, gptEstimateAccepted), {
        onSuccess: (data) => {
          if (typeof data !== 'undefined') {
            notify("Richiesta inviata. Riceverai una mail al termine dell'elaborazione.", 'success')
            setAcceptedCharCheck(false)
            setAcceptedGptCheck(false)
            setFile(null)
            setTimeout(() => history.push('llm-visibility'), 1000)
          } else {
            notify(
              'Richiesta fallita. Qualcosa è andato storto, riprova o contatta un Innovation.',
              'error'
            )
          }
        },
        onError: (errorMessage, err) => handleActionError(err, questionsOnly),
      })
    } catch (err) {
      console.error("Invio del report LLM Visibility fallito:", err)
    }
  }

  const executeExistingAction = async (endpoint, successMessage, questionsOnly = false, gptEstimateAccepted = false) => {
    if (!id || !validateForm(questionsOnly)) {
      return
    }

    if (!questionsOnly && !gptEstimateAccepted) {
      pendingActionRef.current = { action: endpoint, questionsOnly: false, flowNode: null }
    }

    try {
      const response = await upload('post', `llm-visibility/${id}/${endpoint}/`, buildFormData(questionsOnly, gptEstimateAccepted), {
        onSuccess: () => {
          notify(successMessage, 'success')
          setAcceptedCharCheck(false)
          setAcceptedGptCheck(false)
          setFile(null)
          setTimeout(() => history.push('/llm-visibility'), 1000)
        },
        onError: (errorMessage, err) => handleActionError(err, questionsOnly),
      })

      return response
    } catch (err) {
      throw err
    }
  }

  const loadTaggingToolReports = async () => {
    try {
      const res = await api.get('tagging-tool/reports/')
      setTaggingToolReports(res.data || [])
    } catch (err) {
      console.error('Impossibile recuperare i report del Tagging Tool:', err)
      notify('Impossibile caricare i report del Tagging Tool.', 'warning')
    }
  }

  const loadReport = async () => {
    if (!id) return

    try {
      const res = await api.get(`llm-visibility/${id}/`)
      const report = res.data
      setName(report.name)
      setBrand(getLLMVisibilityClientName(report))
      setSelectedClientId(report?.cliente?.id ? String(report.cliente.id) : '')
      setCountry(report.country || 'IT')
      setLanguage(report.language || 'it')
      setUseGpt(report.gpt)
      setUseGemini(report.gemini)
      setUsePerplexity(report.perplexity || false)
      setUseDataforseo(report.use_dataforseo || false)
      setCustomPrompt(report.custom_prompt || DEFAULT_QUESTION_PROMPT)
      setReportStatus(report.status)
      setConnectedTaggingId(report.connected_tagging || '')
      setSelectedTaggingReportId(report.connected_tagging || '')
      setAvailableActions(report.available_actions || [])
    } catch (err) {
      notify('Impossibile caricare i dati del report esistente.', 'error')
    }
  }

  const loadResumeFlowStatus = async () => {
    if (!id) return
    setIsLoadingResumeFlowStatus(true)
    try {
      const res = await api.get(`llm-visibility/${id}/flow_status/`)
      setResumeFlowStatus(res.data || null)
    } catch (err) {
      notify('Impossibile caricare lo stato dettagliato del flusso.', 'warning')
      setResumeFlowStatus(null)
    } finally {
      setIsLoadingResumeFlowStatus(false)
    }
  }

  const handleTagGeneratedQuestions = async () => {
    if (!id || !selectedTaggingReportId) {
      notify('Seleziona prima un report del Tagging Tool.', 'warning')
      return
    }

    setIsSubmittingTagging(true)
    try {
      const response = await api.post(`llm-visibility/${id}/tag_generated_questions/`, {
        mode: taggingMode,
        source_tagging_report_id: selectedTaggingReportId,
        force_regenerate_questions: taggingOptions.forceRegenerateQuestions,
        force_new_clone: taggingOptions.forceNewClone,
        force_retag: taggingOptions.forceRetag,
      })
      setConnectedTaggingId(response.data.connected_tagging_id)
      setIsTaggingModalOpen(false)
      notify(
        taggingMode === 'CLONE'
          ? 'Il tagging delle domande generate è stato messo in coda sul clone selezionato.'
          : 'Il report esistente del Tagging Tool è stato collegato.',
        'success'
      )
      await loadTaggingToolReports()
      await loadReport()
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Impossibile avviare il tagging delle domande generate.'
      notify(errorMessage, 'error')
      console.error('Impossibile avviare il tagging delle domande generate:', err)
    } finally {
      setIsSubmittingTagging(false)
    }
  }

  const handleResumeFromInterruption = async () => {
    if (!id) return
    try {
      await executeExistingAction(
        'resume_from_interruption',
        'Ripresa avviata correttamente.',
        false,
        acceptedGptCheck
      )
    } catch (err) {
      console.error("Impossibile riprendere l'elaborazione:", err)
    }
  }

  const handleOpenResumeFlowModal = async () => {
    if (!id) return
    setIsResumeFlowModalOpen(true)
    await loadResumeFlowStatus()
  }

  const handleResumeFlowNode = async (llm, node, gptEstimateAccepted = false) => {
    if (!id) return
    const nodeKey = `${llm}:${node}`
    if (!gptEstimateAccepted) {
      pendingActionRef.current = {
        action: 'resume_flow_node',
        questionsOnly: false,
        flowNode: { llm, node },
      }
    }

    setResumingFlowNodeKey(nodeKey)
    try {
      await upload('post', `llm-visibility/${id}/resume_flow_node/`, buildResumeFlowFormData(llm, node, gptEstimateAccepted), {
        onSuccess: async () => {
          notify('Ripresa mirata avviata correttamente.', 'success')
          setAcceptedCharCheck(false)
          setAcceptedGptCheck(false)
          await loadReport()
          await loadResumeFlowStatus()
        },
        onError: (errorMessage, err) => {
          if (err.response?.data?.message?.includes('GPT Estimate')) {
            setGptEstimateParams(err.response.data?.estimates)
            setShowGptCheck(true)
            return
          }
          notify(err.response?.data?.error || 'Impossibile avviare la ripresa mirata.', 'warning')
          setAcceptedCharCheck(false)
          setAcceptedGptCheck(false)
        },
      })
    } catch (err) {
      console.error('Impossibile avviare la ripresa mirata:', err)
    } finally {
      setResumingFlowNodeKey('')
    }
  }

  const handleRegenerateQuestions = async () => {
    if (!id) return
    try {
      await executeExistingAction('regenerate_questions', 'Rigenerazione domande avviata.', true)
    } catch (err) {
      console.error('Impossibile rigenerare le domande:', err)
    }
  }

  useEffect(() => {
    if (isWebranking) {
      api
        .get('clienti/')
        .then((response) => {
          setClienti(Array.isArray(response.data) ? response.data : response.data?.results || [])
        })
        .catch((err) => {
          console.error('Impossibile caricare i clienti per LLM Visibility:', err)
        })
    }
    loadTaggingToolReports()
    loadReport()
  }, [isWebranking])

  useEffect(() => {
    if (acceptedGptCheck && !estimateSubmitInFlightRef.current) {
      estimateSubmitInFlightRef.current = true
      const { action, questionsOnly } = pendingActionRef.current
      const flowNode = pendingActionRef.current.flowNode
      let submitPromise
      if (action === 'create') {
        submitPromise = handleCreate(null, questionsOnly, true)
      } else if (action === 'resume_flow_node' && flowNode) {
        submitPromise = handleResumeFlowNode(flowNode.llm, flowNode.node, true)
      } else {
        submitPromise = executeExistingAction(action, 'Ripresa avviata correttamente.', questionsOnly, true)
      }
      if (submitPromise && typeof submitPromise.finally === 'function') {
        submitPromise.finally(() => {
          setAcceptedGptCheck(false)
          estimateSubmitInFlightRef.current = false
        })
      } else {
        setAcceptedGptCheck(false)
        estimateSubmitInFlightRef.current = false
      }
    }
  }, [acceptedGptCheck])

  const confirmEvaluation = (e) => {
    e.preventDefault()
    setShowCharCheck(false)
    setAcceptedCharCheck(true)
  }

  const cancelEvaluation = () => {
    setShowCharCheck(false)
    setAcceptedGptCheck(false)
  }

  const confirmEstimate = (e) => {
    e.preventDefault()
    setShowGptCheck(false)
    setAcceptedGptCheck(true)
  }

  const cancelEstimate = () => {
    setShowGptCheck(false)
    setShowCharCheck(false)
  }


  const hasAction = (action) => (availableActions || []).includes(action)

  const runWorkflowAction = async (action, successMessage) => {
    if (!id) return
    try {
      await api.post(`llm-visibility/${id}/actions/${action}/`)
      notify(successMessage, 'success')
      await loadReport()
    } catch (err) {
      notify(err.response?.data?.error || 'Impossibile avviare l\'azione richiesta.', 'error')
    }
  }
  const connectedTaggingReport = taggingToolReports.find((report) => report.id === connectedTaggingId)

  const getFriendlyRunStatus = (status) => {
    switch (status) {
      case 'EXPORTED_QUESTIONS':
        return 'Domande pronte'
      case 'EXPORTED':
        return 'Report completo esportato'
      case 'ERROR':
        return 'Esecuzione interrotta'
      case 'RETRIEVING_QUESTIONS':
        return 'Generazione domande'
      case 'RETRIEVING_ANSWERS':
        return 'Generazione risposte'
      case 'CREATED':
        return 'Bozza'
      default:
        return status || 'Non avviato'
    }
  }

  const getFriendlyTaggingStatus = (report) => {
    if (!report) return 'Nessun report di tagging collegato'
    if (report.status === 'EXPORTED') return 'Il report collegato è esportato e pronto'
    if (report.status === 'READY_TO_RUN' || report.status === 'RUNNING') return 'Il report collegato è ancora in lavorazione'
    return `Stato report collegato: ${report.status || 'Sconosciuto'}`
  }

  const openTaggingFlow = () => {
    setTaggingMode('CLONE')
    setIsTaggingModalOpen(true)
  }

  const workflowCard = (() => {
    if (!id) {
      return {
        eyebrow: 'Inizia da qui',
        title: "Crea prima l'esecuzione.",
        body: "La creazione salva la configurazione e avvia subito l'esecuzione, senza passaggi separati.",
        primaryLabel: 'Crea report completo',
        primaryColor: 'info',
        primaryAction: (event) => handleCreate(event, false),
        secondary: [
          { label: 'Genera solo domande', action: (event) => handleCreate(event, true), color: 'secondary' },
        ],
      }
    }

    if (reportStatus === 'ERROR') {
      return {
        eyebrow: 'Attenzione',
        title: "Riprendi l'esecuzione in modo mirato.",
        body: 'Apri lo stato dettagliato per provider e rilancia solo il nodo necessario, evitando chiamate API superflue sugli altri LLM.',
        primaryLabel: 'Apri stato dettagliato',
        primaryColor: 'warning',
        primaryAction: handleOpenResumeFlowModal,
        primaryDisabled: false,
        secondary: [],
      }
    }

    if (reportStatus === 'EXPORTED_QUESTIONS') {
      return {
        eyebrow: 'Passo consigliato',
        title: 'Tagga le domande generate.',
        body: connectedTaggingReport
          ? 'Hai già un report di tagging collegato. Riapri il flusso se vuoi cambiare report, creare un nuovo clone o rilanciare il tagging.'
          : 'Le domande sono pronte. Collega o clona un report del Tagging Tool per arricchirle prima di mandarle ai sistemi a valle.',
        primaryLabel: 'Tagga domande generate',
        primaryColor: 'primary',
        primaryAction: openTaggingFlow,
        secondary: [
          { label: 'Esegui analisi completa', action: handleResumeFromInterruption, color: 'info' },
          { label: 'Rigenera domande', action: handleRegenerateQuestions, color: 'secondary' },
        ],
      }
    }

    if (reportStatus === 'EXPORTED') {
      return {
        eyebrow: connectedTaggingReport ? 'Tagging collegato' : 'Arricchimento opzionale',
        title: connectedTaggingReport ? 'Rivedi o cambia il flusso di tagging.' : 'Collega il tagging se vuoi viste arricchite.',
        body: connectedTaggingReport
          ? 'Questa esecuzione punta già a un report del Tagging Tool. Da qui puoi sostituirlo, clonare da una nuova sorgente o rilanciare il tagging.'
          : "L'analisi è completa. Il tagging è opzionale, ma è il modo più rapido per arricchire la visibilità delle domande con la tua tassonomia esistente.",
        primaryLabel: connectedTaggingReport ? 'Gestisci flusso di tagging' : 'Tagga domande generate',
        primaryColor: 'primary',
        primaryAction: openTaggingFlow,
        secondary: [
          hasAction('RERUN_BRAND_EXTRACTION') && {
            label: 'Rilancia brand extraction',
            action: () => runWorkflowAction('RERUN_BRAND_EXTRACTION', 'Brand extraction avviata.'),
            color: 'secondary',
          },
          hasAction('RERUN_TOPIC_EXTRACTION') && {
            label: 'Rilancia topic extraction',
            action: () => runWorkflowAction('RERUN_TOPIC_EXTRACTION', 'Topic extraction avviata.'),
            color: 'secondary',
          },
        ].filter(Boolean),
      }
    }

    return {
      eyebrow: 'In corso',
      title: 'Questa esecuzione è ancora in corso.',
      body: 'Aspetta che finiscano generazione domande o esportazione risposte prima di avviare passaggi successivi come il tagging.',
      primaryLabel: 'Attendi completamento',
      primaryColor: 'light',
      primaryAction: () => {},
      primaryDisabled: true,
      secondary: [],
    }
  })()

  return (
    <Header
      iconClass="ni ni-align-left-2 text-white"
      title="LLM Visibility"
      description={'Dato un set di parole chiave, genera domande per gli LLM e controlla quanto i brand vengono citati in esse'}
    >
      <div className="pb-5 llm-visibility-page">
        <Card className="bg-secondary shadow">
          <CardHeader className="bg-white border-0">
            <Row className="align-items-center">
              <Col md={10}>
                <h1 className="mb-0">Report LLM Visibility</h1>
              </Col>
              <Col md={2} style={{ textAlign: 'right' }}>
                <Button color="primary" onClick={() => history.push('/llm-visibility')}>&larr; Torna alla lista</Button>
              </Col>
            </Row>
          </CardHeader>
          <CardBody>
            <CharCheckModal show={showCharCheck} onHide={cancelEvaluation} onSubmit={confirmEvaluation} errCharList={errCharList} withExclusion={true} />
            <GPTEstimateModal show={showGptCheck} onHide={cancelEstimate} onSubmit={confirmEstimate} params={gptEstimateParams} isLLMVisibility/>
            <TagGeneratedQuestionsModal
              isOpen={isTaggingModalOpen}
              toggle={() => setIsTaggingModalOpen(!isTaggingModalOpen)}
              reports={taggingToolReports}
              mode={taggingMode}
              onModeChange={setTaggingMode}
              selectedReportId={selectedTaggingReportId}
              onSelectedReportChange={setSelectedTaggingReportId}
              options={taggingOptions}
              onOptionsChange={setTaggingOptions}
              onSubmit={handleTagGeneratedQuestions}
              submitting={isSubmittingTagging}
              connectedReport={connectedTaggingReport}
              tooltipPrefix={`llm-visibility-form-${id || 'new'}`}
            />
            <LLMResumeFlowModal
              isOpen={isResumeFlowModalOpen}
              toggle={() => setIsResumeFlowModalOpen(!isResumeFlowModalOpen)}
              flowStatus={resumeFlowStatus}
              loading={isLoadingResumeFlowStatus}
              onRefresh={loadResumeFlowStatus}
              onResumeNode={handleResumeFlowNode}
              runningNodeKey={resumingFlowNodeKey}
            />
            {loading ? (
              <div className="text-center">
                <Spinner animation="border" role="status">
                  <span className="sr-only">Caricamento...</span>
                </Spinner>
              </div>
            ) : (
              <Form onSubmit={id ? (event) => event.preventDefault() : (event) => handleCreate(event, false)} id="llm-visibility-form" action="">
                <h6 className="heading-small text-muted mb-4">Configurazione dell'analisi</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label htmlFor="name" className="form-control-label">
                        Nome dell'analisi
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="text"
                        placeholder="es. il nome del marchio"
                        id="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                      />
                      <FormText color="muted">
                        Il dataset BigQuery sarà nominato con il nome inserito + "_LLM".<br />
                        {name ? (
                          <span> Anteprima: <strong>{name}_LLM</strong></span>
                        ) : null}
                      </FormText>
                    </FormGroup>
                    {isWebranking ? (
                      <ClienteField
                        clienti={clienti}
                        selectedClientId={selectedClientId}
                        setSelectedClientId={setSelectedClientId}
                        setBrand={setBrand}
                      />
                    ) : (
                      <FormGroup>
                        <label htmlFor="brand" className="form-control-label">
                          Marchio
                        </label>
                        <Input
                          className="form-control-alternative"
                          type="text"
                          placeholder="es. il nome del marchio"
                          id="brand"
                          value={brand}
                          onChange={(e) => setBrand(e.target.value)}
                        />
                      </FormGroup>
                    )}
                    <LocalizationFields
                      country={country}
                      setCountry={setCountry}
                      language={language}
                      setLanguage={setLanguage}
                      countryId="country"
                      languageId="language"
                    />
                  </Col>
                  <Col md={6}>
                    <FileInput
                      onFileChange={setFile}
                      accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                      helpText={id
                        ? <>Carica un nuovo file Excel per sostituire le parole chiave. <br /> <b>Lascia vuoto per rieseguire con le parole chiave originali.</b></>
                        : <>Carica un file Excel contenente le tue parole chiave. <br /> <b>E' richiesta una colonna chiamata: "Keyword"</b></>
                      }
                      required={!id}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <FormGroup>
                      <label htmlFor="custom-prompt" className="form-control-label">
                        Prompt personalizzato (opzionale)
                      </label>
                      <Input
                        className="form-control-alternative"
                        type="textarea"
                        id="custom-prompt"
                        rows="4"
                        placeholder="Sovrascrivi il prompt di default per la generazione delle domande. Usa {keyword} come segnaposto."
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value)}
                      />
                      <FormText color="muted">
                        Lascia vuoto per usare il prompt di default. Se inserito, deve contenere la variabile {'{keyword}'}.
                      </FormText>
                    </FormGroup>
                  </Col>
                </Row>
                <hr className="my-4" />
                <h6 className="heading-small text-muted mb-4">LLM e collegamenti</h6>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <label className="form-control-label">
                        LLM da usare
                      </label>
                      <div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="gpt-checkbox"
                            type="checkbox"
                            checked={useGpt}
                            onChange={() => setUseGpt(!useGpt)}
                          />
                          <label className="custom-control-label" htmlFor="gpt-checkbox">
                            OpenAI GPT
                          </label>
                        </div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="gemini-checkbox"
                            type="checkbox"
                            checked={useGemini}
                            onChange={() => setUseGemini(!useGemini)}
                          />
                          <label className="custom-control-label" htmlFor="gemini-checkbox">
                            Google Gemini
                          </label>
                        </div>
                        <div className="custom-control custom-checkbox custom-control-inline">
                          <Input
                            className="custom-control-input"
                            id="perplexity-checkbox"
                            type="checkbox"
                            checked={usePerplexity}
                            onChange={() => setUsePerplexity(!usePerplexity)}
                          />
                          <label className="custom-control-label" htmlFor="perplexity-checkbox">
                            Perplexity
                          </label>
                        </div>
                        {(useGpt || useGemini) && (
                          <div className="mt-3">
                            <label className="form-control-label d-block mb-2">
                              Modalità di esecuzione (GPT e Gemini)
                            </label>
                            <div className="custom-control custom-radio custom-control-inline">
                              <Input
                                className="custom-control-input"
                                id="llm-direct"
                                name="llm-mode"
                                type="radio"
                                checked={!useDataforseo}
                                onChange={() => setUseDataforseo(false)}
                              />
                              <label className="custom-control-label" htmlFor="llm-direct">
                                API diretta
                              </label>
                            </div>
                            <div className="custom-control custom-radio custom-control-inline">
                              <Input
                                className="custom-control-input"
                                id="llm-dataforseo"
                                name="llm-mode"
                                type="radio"
                                checked={useDataforseo}
                                onChange={() => setUseDataforseo(true)}
                              />
                              <label className="custom-control-label" htmlFor="llm-dataforseo">
                                DataForSEO Scraper
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    </FormGroup>
                  </Col>
                  <Col md={6} className="d-flex flex-column">
                    <div className="llm-visibility-workflow">
                      <small className="llm-visibility-workflow__eyebrow">{workflowCard.eyebrow}</small>
                      <div className="llm-visibility-workflow__title">{workflowCard.title}</div>
                      <p className="llm-visibility-workflow__body">{workflowCard.body}</p>

                      <div className="llm-visibility-workflow__actions">
                        <Button
                          color={workflowCard.primaryColor || 'info'}
                          onClick={workflowCard.primaryAction}
                          disabled={workflowCard.primaryDisabled}
                          type="button"
                        >
                          {workflowCard.primaryLabel}
                        </Button>
                        {workflowCard.secondary.length > 0 && (
                          <div className="llm-visibility-workflow__secondary">
                            {workflowCard.secondary.map((action) => (
                              <Button
                                key={action.label}
                                color={action.color}
                                onClick={action.action}
                                type="button"
                              >
                                {action.label}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="llm-visibility-workflow__meta">
                        <div>
                          <span className="llm-visibility-workflow__meta-label">Stato esecuzione</span>
                          <div className="llm-visibility-workflow__meta-value">{getFriendlyRunStatus(reportStatus)}</div>
                        </div>
                        <div>
                          <span className="llm-visibility-workflow__meta-label">Stato tagging</span>
                          <div className="llm-visibility-workflow__meta-value">{getFriendlyTaggingStatus(connectedTaggingReport)}</div>
                        </div>
                        <div>
                          <span className="llm-visibility-workflow__meta-label">Report collegato</span>
                          <div className="llm-visibility-workflow__meta-value">
                            {connectedTaggingReport ? connectedTaggingReport.name : 'Non ancora collegato'}
                          </div>
                          {connectedTaggingReport && (
                            <button
                              className="btn btn-link p-0 llm-visibility-workflow__link"
                              onClick={() => history.push(`/reporting/reports/seo/${connectedTaggingReport.id}`)}
                              type="button"
                            >
                              Apri report di tagging
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
            <ToastContainer />
          </CardBody>
        </Card>
      </div>
    </Header>
  )
}

export default LLMVisibilityForm


